import { useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type CurrentUser } from '../api';
import { Badge } from './ui';
import { AccountMenu } from './AccountMenu';
import { NotificationBell } from './NotificationBell';
import { Sidebar } from './Sidebar';
import { IconMenu } from './icons';
import { FeedbackWidget } from './FeedbackWidget';
import { isApprovedViewer } from '../roles';

const ROLE_LABEL: Record<CurrentUser['role'], string> = {
  PENDING: 'Pending access',
  REQUESTER: 'Requester',
  APPROVER: 'Approver',
  ADMIN: 'Administrator',
  SUPER_ADMIN: 'Super Admin',
  SYSTEM_ADMIN: 'System administrator',
  MTN_TECHNICIAN: 'MTN Technician',
  APPROVED_VIEWER: 'Approved requests viewer',
};

export function AppShell({ user, children }: { user: CurrentUser; children: ReactNode }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function signOut() {
    const { redirectTo } = await api.logout();
    window.location.href = redirectTo;
  }

  return (
    <div className="flex min-h-full">
      {/* A hairline of brand across the very top, rather than a coloured bar:
          it reads as identity without spending contrast the content needs.
          Fixed and full-bleed, so it crosses the sidebar too instead of
          starting halfway along where the content column begins. */}
      <div
        className="fixed inset-x-0 top-0 z-50 h-0.5 bg-gradient-to-r from-brand-700 via-brand-500 to-copper-500"
        aria-hidden="true"
      />

      <Sidebar user={user} open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* min-w-0: without it a wide table inside main stretches the grid track
          and the whole page scrolls sideways instead of the table. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Nothing but the controls that belong to *you*: the destinations all
            moved into the column on the left. */}
        <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/80 backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-950/80">
          <div className="flex h-14 items-center gap-2 px-4 sm:px-6">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-controls="app-sidebar"
              aria-expanded={menuOpen}
              className="-ml-1.5 rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:hidden dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <IconMenu className="h-5 w-5" />
            </button>

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {/* Nothing is ever addressed to the viewer, and the bell's
                  own fetch is closed to that role. */}
              {!isApprovedViewer(user.role) && <NotificationBell />}
              <AccountMenu user={user} onSignOut={() => void signOut()} />
            </div>
          </div>
        </header>

        {/* Keyed on the path so each navigation replays the entrance rather than
            swapping content in place with no sense of having moved. */}
        <main key={pathname} className="animate-rise mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
          {children}
        </main>

        <footer className="border-t border-slate-200 dark:border-slate-800">
          <div className="mx-auto max-w-6xl px-4 py-5 text-xs text-slate-500 sm:px-6 dark:text-slate-400">
            New Horizon Copper: IT enrolment and offboarding
          </div>
        </footer>
      </div>

      {/* The viewer role does nothing but read, and that includes this. */}
      {!isApprovedViewer(user.role) && <FeedbackWidget />}
    </div>
  );
}

export function RoleBadge({ role }: { role: CurrentUser['role'] }) {
  const tone =
    role === 'SYSTEM_ADMIN' || role === 'SUPER_ADMIN'
      ? 'danger'
      : role === 'ADMIN'
        ? 'copper'
        : role === 'APPROVER'
          ? 'brand'
          : role === 'MTN_TECHNICIAN'
            ? 'info'
            : role === 'PENDING'
              ? 'warning'
              : 'neutral';
  return (
    <Badge tone={tone} dot>
      {ROLE_LABEL[role]}
    </Badge>
  );
}
